import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import useDailyChecks from "../../data/useDailyChecks";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ICreateUpdateDailyCheckStepRequest, IDailyCheckStep } from "../../models/DailyChecks";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string,
  order: number,
  isActive: boolean,
};

interface DailyCheckStepModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  definitionId: string;
  step?: IDailyCheckStep;
  defaultOrder: number;
}

export default function DailyCheckStepModal({ open, setOpen, definitionId, step, defaultOrder }: DailyCheckStepModalProps) {
  const stepLabel = "Daily Check Step";
  const { saveHandlers } = useDefaultCRUDHandlers(stepLabel);
  const { deleteHandlers } = useDefaultCRUDHandlers(stepLabel);
  const { createStep, updateStep, deleteStep } = useDailyChecks();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    const updatedStep: ICreateUpdateDailyCheckStepRequest = {
      ...step,
      dailyCheckDefinitionId: step?.dailyCheckDefinitionId ?? definitionId,
      name: data.name,
      order: Number(data.order),
      isActive: data.isActive,
    };

    if (step !== undefined) {
      updateStep.mutate(updatedStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      createStep.mutate(updatedStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    deleteStep.mutate(step?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (step !== undefined) {
      reset({
        name: step.name,
        order: step.order,
        isActive: step.isActive,
      });
    } else {
      reset({
        name: "",
        order: defaultOrder,
        isActive: true,
      });
    }
  }, [step, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={`Delete ${stepLabel}`} message={`Are you sure you want to delete this ${stepLabel.toLowerCase()}? This action cannot be undone.`}
        confirmButtonText="Delete" onConfirm={handleDelete} isLoading={deleteStep.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{`${step ? "Edit" : "Add"} a ${stepLabel}`}</h1>
        <h1 className="text-sm text-gray-500">{`All ${stepLabel.toLowerCase()}s will need to be marked as a pass or fail each day.`}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} required={true} />
              <input type="hidden" {...register("order")} />

              <SwitchInput label="Is Active" registerName="isActive" register={register} setValue={setValue} watch={watch} />

              <ButtonInput
                label={`${step ? "Save Changes" : "Create"}`}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createStep.isLoading || updateStep.isLoading}
              />
              {step !== undefined && (
                <ButtonInput label="Delete" isSubmit={false} onClick={() => (setDeleteOpen(true))} classes="!bg-red-500 hover:!bg-red-600" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
